// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Ellipsis from 'components/treactui-template/atoms/Ellipsis';
import { AppRoutes, ProjectSubPath } from 'utilities/router/AppRoutes';
import { InfoTag } from 'components/treactui-template/atoms/InfoTag';
import { ProjectMetadata } from 'model/ProjectMetadata';
import { loadArrayIntoImageElement } from 'components/treactui-template/meta/loadImageIntoElement';
import useProjectDetailsNavigation from 'hooks/useProjectDetailsNavigation';
import { useAuthContext } from '@tls/auth';
import { StrategyIdentifier } from 'components/project/settings/subcomponents/StrategySelector';
import styled, { css } from 'styled-components';
import { Colors } from '@tls/treact-ui';
import { GetStatusTranslationKey, ProjectStatusIcon } from 'model/ProjectMetaMessageExtensions';
import Options from '../Options';
import { AnimationCss } from 'components/treactui-template/atoms/animation/AnimatePulse';
import IconImage from 'components/treactui-template/icons/IconImage';
import IconLabel from 'components/treactui-template/icons/IconLabel';
import IconVersion from 'components/treactui-template/icons/IconVersion';
import LockedInfo from './LockedInfo';
import BackdropLoading from 'components/treactui-template/organisms/backdrop/loading/BackdropLoading';
import { UseCase } from '@tls/sw91-communication/types/com.base';
import { useAppSelector } from 'store/hooks';

export type TileProps = {
  entry: ProjectMetadata;
};

const timeFormat: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeStyle: 'short',
};

export default function Tile({ entry }: Readonly<TileProps>) {
  const { t, i18n } = useTranslation();

  const { navigateToDetails, loading } = useProjectDetailsNavigation();

  // While DbAccess is initializing, a (possibly cached) tile stays in a loading state until its
  // project has been reported ready.
  const isInitializing = useAppSelector(s => s.projects.isInitializing);
  const isReady = useAppSelector(s => s.projects.readyProjectIds.includes(entry.projectId ?? ''));
  const initLoading = isInitializing && !isReady;

  const goToDetails = useCallback(() => {
    // The tile is covered by the BackdropLoading — ignore clicks until it is gone.
    if (loading || initLoading) return;
    navigateToDetails(AppRoutes.ProjectWithId(entry.projectId ?? '', ProjectSubPath.Label), entry.projectId);
  }, [entry.projectId, navigateToDetails, loading, initLoading]);
  const projectUsecase = entry.projectUseCase?.useCase;

  const tags =
    entry.tags?.map(t => (
      <InfoTag key={t}>
        <span className='font-size_xs font-weight_semibold'>{t}</span>
      </InfoTag>
    )) ?? [];

  const strategy = (
    <InfoTag $backgroundColor={Colors.trblue4.hex} $color={Colors.trwhite.hex}>
      <span className='font-size_xs font-weight_semibold'>
        {StrategyIdentifier({ projectStrategy: entry.strategyType })}
      </span>
    </InfoTag>
  );

  const imageElement = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (!entry.projectId || !entry.previewImage?.previewImageData || entry.previewImage.previewImageData.length < 1) {
      return;
    }

    loadArrayIntoImageElement([entry.previewImage.previewImageData as Uint8Array<any>], 'image/png', imageElement);
  }, [entry?.previewImage?.previewImageData, entry.projectId]);

  const lastExport =
    !entry.strategyExportTime || entry.strategyExportTime.getTime() <= 0 ? null : entry.strategyExportTime;

  const { appBaseUrl } = useAuthContext();
  return (
    <Container onClick={goToDetails} data-testid={`projects-project-${entry.projectId ?? 'unknown'}-tile`}>
      <BackdropLoading
        loading={loading || initLoading}
        label={loading ? t('project.actions.lock.aquire') : t('project.loading')}
        spinner
      />
      <Header data-testid={`projects-project-${entry.projectId ?? 'unknown'}-status`}>
        <ProjectStatusIcon status={entry.projectStatus} width={'1.5rem'} />
        <div>{t(GetStatusTranslationKey(entry.projectStatus))}</div>
        <Options projectId={entry.projectId ?? ''} projectName={entry.projectName ?? ''} lockedBy={entry.lockedBy} />
      </Header>
      <Title>
        <LockedInfo lockedBy={entry.lockedBy} />
        <Ellipsis className='font-size_xl'>{entry.projectName}</Ellipsis>
      </Title>
      <Divider />
      <Details>
        <TagDetail>
          <span className='tall'>{t('project.tag.title')}</span>
          <Items>
            <Item>
              <HideOverflow />
              {tags}
            </Item>
            {projectUsecase === UseCase.COMPONENT_DETECTION && <div>{strategy}</div>}
          </Items>
        </TagDetail>
        <EntryInfo>
          <Info>
            <span className='tall'>{t('project.lastExport')}</span>
            <Ellipsis className='font-size_xs pushRight'>
              {lastExport?.toLocaleString(i18n.language, timeFormat) ?? t('project.notExported')}
            </Ellipsis>
          </Info>
          <Info>
            <span className='tall'>{t('project.lastModified')}</span>
            <Ellipsis className='font-size_xs pushRight'>
              {entry.modifyTime?.toLocaleString(i18n.language, timeFormat)}
            </Ellipsis>
          </Info>
        </EntryInfo>
      </Details>

      <Divider />
      <Image
        src={`${appBaseUrl}/images/image-placeholder.png`}
        ref={imageElement}
        alt='preview'
        data-testid={`projects-project-${entry.projectId ?? 'unknown'}-preview`}
      />
      <ImageDetails>
        <ImageDetail $color={(entry.numberImages ?? 0) > 0 ? 'current' : Colors.trgrey5.hex}>
          <IconImage width='1.5rem' />
          <span>{entry.numberImages}</span>
        </ImageDetail>
        <ImageDetail $color={(entry.numberLabels ?? 0) > 0 ? 'current' : Colors.trgrey5.hex}>
          <IconLabel width='1.5rem' />
          <span>{entry.numberLabels}</span>
        </ImageDetail>
        <ImageDetail $color={entry.projectVersion ? 'current' : Colors.trgrey5.hex}>
          <IconVersion width='1.5rem' />
          <span>{entry.projectVersion ?? '-'}</span>
        </ImageDetail>
      </ImageDetails>
    </Container>
  );
}

const Image = styled.img`
  height: 10rem;
  min-height: 10rem;
  width: 100%;
  min-width: 100%;
  background-color: rgba(${Colors.trgrey2.rgb}, 0.4);
  ${AnimationCss}
`;

const Title = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 0.5rem;
  height: 2.5rem;
`;

const ImageDetails = styled.div`
  display: flex;
  column-gap: 0.75rem;
`;

const ImageDetail = styled.div<{ $color?: string }>`
  display: flex;
  flex-direction: column;
  column-gap: 0.5rem;
  align-items: center;
  ${({ $color }) => css`
    color: ${$color};
  `}
`;

const Container = styled.div`
  position: relative;
  height: 26.5rem;
  width: 17rem;
  min-width: 17rem;
  cursor: pointer;
  box-shadow:
    0 2px 4px 0 rgba(0, 0, 0, 0.05),
    0 2px 8px 0 rgba(161, 161, 161, 0.4);
  padding: 0 0.5rem;
  border: 1px solid ${Colors.trgrey5.hex};

  ${({ theme }) => css`
    @media (hover: hover) {
      &:hover {
        box-shadow:
          inset 0 0 0 1px ${theme.trcolor1.hex},
          0 0 3px 0 ${theme.trcolor3.hex};
      }
    }
  `}
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  column-gap: 0.5rem;
  .moveRight {
    margin-right: -0.5rem;
  }
`;

const Divider = styled.div`
  border-bottom: 1px solid ${Colors.trgrey5.hex};
  margin: 0.5rem 0;
`;

const Details = styled.div`
  display: grid;
  row-gap: 0.25rem;
  grid-auto-rows: min-content;
  span > {
    line-height: 1.5rem;
  }
  .pushRight {
    text-align: right;
    line-height: 1.5rem;
  }
  .tall {
    height: 1.5rem;
  }
  span {
    line-height: 1.5rem;
  }
`;

const TagDetail = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  overflow: hidden;
`;

const HideOverflow = styled.div`
  background: linear-gradient(to left, rgba(${Colors.trwhite.rgb}, 0.02) 0, ${Colors.trwhite.hex} 100%);
  position: absolute;
  float: left;
  left: 0;
  height: 100%;
  width: 1rem;
`;

const Items = styled.div`
  position: relative;
  margin-left: 0.5rem;
  flex: 1;
  display: flex;
  overflow: hidden;
  gap: 0.5rem;
`;

const Item = styled.div`
  flex: 1;
  display: flex;
  gap: 0.25rem;
  justify-content: end;
  overflow: hidden;
`;

const EntryInfo = styled.div`
  display: grid;
  row-gap: 0.25rem;
  grid-auto-rows: min-content;
`;

const Info = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.25rem;
  align-items: center;
`;
